import React, { useContext, useEffect, useReducer, useState } from 'react';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import { toast } from 'react-toastify';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, orders: action.payload, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_ORDER':
      return {
        ...state,
        orders: state.orders.map((o) =>
          o._id === action.payload._id ? action.payload : o
        ),
      };
    default:
      return state;
  }
};

export default function AdminOrderListScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [{ loading, error, orders }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    orders: [],
  });

  // Reject modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingOrderId, setRejectingOrderId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // order id being actioned

  // Expand/collapse order items
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Filter state for summary badges
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const { data } = await axios.get('/api/orders', {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    fetchData();
  }, [userInfo]);

  const handleApprove = async (orderId) => {
    if (!window.confirm('Approve this order? Stock will be decremented.')) return;
    setActionLoading(orderId);
    try {
      const { data } = await axios.put(
        `/api/orders/${orderId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      dispatch({ type: 'UPDATE_ORDER', payload: data.order });
      toast.success('Order approved and stock updated!');
    } catch (err) {
      toast.error(getError(err));
    }
    setActionLoading(null);
  };

  const openRejectModal = (orderId) => {
    setRejectingOrderId(orderId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    setActionLoading(rejectingOrderId);
    try {
      const { data } = await axios.put(
        `/api/orders/${rejectingOrderId}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      dispatch({ type: 'UPDATE_ORDER', payload: data.order });
      toast.success('Order rejected.');
    } catch (err) {
      toast.error(getError(err));
    }
    setShowRejectModal(false);
    setActionLoading(null);
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger',
    };
    return (
      <Badge bg={map[status] || 'secondary'}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
      </Badge>
    );
  };

  const toggleFilter = (status) => {
    setFilterStatus((prev) => (prev === status ? 'all' : status));
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : filterStatus === 'pending'
      ? orders.filter(o => !o.status || o.status === 'pending')
      : orders.filter(o => o.status === filterStatus);

  return (
    <div style={{ padding: '0 8px' }}>
      <h1 style={{ marginBottom: '1.5rem' }}>All Orders</h1>

      {loading ? (
        <LoadingBox />
      ) : error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : orders.length === 0 ? (
        <MessageBox>No orders found.</MessageBox>
      ) : (
        <>
          {/* Summary badges */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <span 
              className="p-2 rounded" 
              style={{ background: '#fff3cd', fontWeight: 600, cursor: 'pointer', border: filterStatus === 'pending' ? '2px solid #ffc107' : '2px solid transparent' }}
              onClick={() => toggleFilter('pending')}
            >
              ⏳ Pending: {orders.filter((o) => !o.status || o.status === 'pending').length}
            </span>
            <span 
              className="p-2 rounded" 
              style={{ background: '#d1e7dd', fontWeight: 600, cursor: 'pointer', border: filterStatus === 'approved' ? '2px solid #198754' : '2px solid transparent' }}
              onClick={() => toggleFilter('approved')}
            >
              ✅ Approved: {orders.filter((o) => o.status === 'approved').length}
            </span>
            <span 
              className="p-2 rounded" 
              style={{ background: '#f8d7da', fontWeight: 600, cursor: 'pointer', border: filterStatus === 'rejected' ? '2px solid #dc3545' : '2px solid transparent' }}
              onClick={() => toggleFilter('rejected')}
            >
              ❌ Rejected: {orders.filter((o) => o.status === 'rejected').length}
            </span>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-dark">
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total (EGP)</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <React.Fragment key={order._id}>
                    <tr>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {order.createdAt?.substring(0, 10)}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {order.user?.name || order.shippingAddress?.fullName}
                        </div>
                        <small className="text-muted">{order.user?.email}</small>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() =>
                            setExpandedOrder(
                              expandedOrder === order._id ? null : order._id
                            )
                          }
                        >
                          {order.orderItems?.length} item(s){' '}
                          {expandedOrder === order._id ? '▲' : '▼'}
                        </Button>
                      </td>
                      <td style={{ fontWeight: 700 }}>
                        {order.totalPrice?.toFixed(2)}
                      </td>
                      <td>
                        <Badge bg="secondary">{order.paymentMethod}</Badge>
                      </td>
                      <td>{getStatusBadge(order.status)}</td>
                      <td>
                        {(!order.status || order.status === 'pending') ? (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <Button
                              size="sm"
                              variant="success"
                              disabled={actionLoading === order._id}
                              onClick={() => handleApprove(order._id)}
                            >
                              {actionLoading === order._id ? '…' : 'Approve'}
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={actionLoading === order._id}
                              onClick={() => openRejectModal(order._id)}
                            >
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                            {order.status === 'approved' ? `✅ ${order.deliveredAt?.substring(0, 10) || ''}` : '❌ Rejected'}
                          </span>
                        )}
                      </td>
                    </tr>

                    {/* Expanded items row */}
                    {expandedOrder === order._id && (
                      <tr>
                        <td colSpan={7} style={{ background: '#f9f9f9', padding: '12px 24px' }}>
                          <strong>Shipping:</strong>{' '}
                          {order.shippingAddress?.fullName},{' '}
                          {order.shippingAddress?.streetName},{' '}
                          {order.shippingAddress?.area},{' '}
                          {order.shippingAddress?.city} —{' '}
                          📞 {order.shippingAddress?.mobileNumber}
                          <table className="table table-sm mt-2 mb-0">
                            <thead>
                              <tr>
                                <th>Product</th>
                                <th>Size</th>
                                <th>Color</th>
                                <th>Qty</th>
                                <th>Unit Price</th>
                                <th>Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.orderItems?.map((item, idx) => (
                                <tr key={idx}>
                                  <td>{item.name}</td>
                                  <td>{item.size}</td>
                                  <td>{item.color || 'Not recorded'}</td>
                                  <td>{item.quantity}</td>
                                  <td>{item.price?.toFixed(2)} EGP</td>
                                  <td>
                                    {(item.price * item.quantity).toFixed(2)} EGP
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {order.rejectionReason && (
                            <p className="text-danger mt-1 mb-0">
                              <strong>Rejection reason:</strong> {order.rejectionReason}
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reject Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Reason (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              placeholder="e.g. Out of stock, payment issue…"
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={actionLoading === rejectingOrderId}
            onClick={handleReject}
          >
            {actionLoading === rejectingOrderId ? 'Rejecting…' : 'Confirm Reject'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
